import React, {
    useEffect, useCallback, useMemo
} from 'react';
import PropTypes from 'prop-types';
import { Map as IMap } from 'immutable';
import { intlShape, injectIntl, FormattedMessage } from 'react-intl';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { change, getFormValues } from 'redux-form/immutable';
import { icons } from 'cstar-react-primitives/lib/redux-form/icons/iconConst';
import { Container } from 'cstar-react-primitives/lib/redux-form/layout/Container';
import { TertiaryButton } from 'cstar-react-primitives/lib/redux-form/buttons/TertiaryButton';
import {
    NEW_ITEM_FORM_NAME,
    DIGITAL_GREETINGS_FIELD,
    CURRENCY_FIELD
} from '../item/newItemForm';
import FaceplateImg from '../faceplatePresentation/FaceplateImg';
import { sampleGreetings } from './tests/sampleGreetings';
import messageIntl from './messageIntl';
import { formatSelectedGreetingOrder, getAmountValue } from './digitalGreetingsUtils';
import { logError } from '../utils/errorUtils';
import { itemSegmentData } from '../segment/segmentSelectors';



const SelectDigitalGreetings = ({
    formName,
    setIsGreetingSelected,
    intl
}) => {
    const dispatch = useDispatch();
    const store = useStore();
    const formValues = useSelector(state => getFormValues(formName)(state));
    const selectedCurrency = formValues ? formValues.get(CURRENCY_FIELD) : '';

    const selectBtnProps = useMemo(() => ({
        btnText: intl.formatMessage(messageIntl.digitalGreetingsSelectLink),
        cashbotName: 'digital-greetings-select-link',
        icon: icons.KEYBOARD_RIGHT,
        isSmall: false,
        isLeftAligned: false
    }), [intl]);

    const previewCards = useMemo(() => sampleGreetings.map(greeting => (
        <FaceplateImg
            key={greeting.get('code')}
            faceplate={greeting}
            cssClass={`digital-greetings-select__preview-card ${greeting.get('className')}`}
        />
    )), []);

    const handleGreetingsOrderCompleted = useCallback((order) => {
        try {
            const formattedOrder = formatSelectedGreetingOrder(order, selectedCurrency);
            dispatch(change(formName, DIGITAL_GREETINGS_FIELD, IMap(formattedOrder)));
            const currentFormValues = getFormValues(formName)(store.getState());
            const segmentData = itemSegmentData(currentFormValues);
            dispatch({
                type: 'DIGITAL_GREETING_ADDED',
                meta: {
                    segment: {
                        eventAction: 'Digital Greeting Added',
                        eventData: {
                            ...segmentData
                        }
                    }
                }
            });
            setIsGreetingSelected(true);
        } catch (error) {
            logError('Error processing greeting order', error);
            setIsGreetingSelected(false);
        }
    }, [dispatch, formName, setIsGreetingSelected]);

    useEffect(() => {
        if (!window || !window.addACardClient) {
            return () => {};
        }

        dispatch(change(formName, DIGITAL_GREETINGS_FIELD, null));
        const containerId = 'digital-greetings-select-content-action';
        const button = document.getElementById(containerId);

        const handleButtonClick = (event) => {
            event.preventDefault();
            try {
                const chooseGreetingSegmentData = {
                    title: intl.formatMessage(messageIntl.digitalGreetingsSelectLink),
                    referrer: '',
                    url: window.location.href || '',
                    path: window.location.pathname || '',
                    destination: 'POP-UP'
                };
                dispatch({
                    type: 'CHOOSE_GREETING_LINK_CLICK',
                    meta: {
                        segment: {
                            eventAction: 'Choose Greeting Card',
                            eventData: {
                                ...chooseGreetingSegmentData
                            }
                        }
                    }
                });
            } catch (error) {
                logError('Error dispatching choose greeting link click:', error);
            }

            try {
                window.addACardClient.addCard();
                window.addACardClient.on('order-completed', handleGreetingsOrderCompleted);
            } catch (err) {
                logError('Failed to open AddACard interface:', err);
            }
        };

        const handleClose = () => {
            window.addACardClient.off('order-completed', handleGreetingsOrderCompleted);
        };

        (async () => {
            if (window.addACardClient) {
                try {
                    await window.addACardClient.load();
                    if (button) {
                        button.addEventListener('click', handleButtonClick);
                        window.addACardClient.on('close', handleClose);
                    }
                } catch (err) {
                    logError('Failed to load AddACard client:', err);
                }
            }
        })();

        return () => {
            if (button) {
                button.removeEventListener('click', handleButtonClick);
            }
            if (window.addACardClient) {
                window.addACardClient.off('order-completed', handleGreetingsOrderCompleted);
                window.addACardClient.off('close', handleClose);
            }
        };
    }, [dispatch, formName, handleGreetingsOrderCompleted]);

    return (
        <div className='digital-greetings'>
            <div className='digital-greetings-label'>
                <FormattedMessage {...messageIntl.digitalGreetingsSelectLabel} values={{ price: getAmountValue(intl, selectedCurrency) }} />
            </div>
            <Container customClassName='digital-greetings-select'>
                <div
                    className='digital-greetings-select__preview'
                    role="region"
                >
                    {previewCards}
                </div>
                <div className='digital-greetings-select__content'>
                    <FormattedMessage {...messageIntl.digitalGreetingsSelectHelper} />
                    <div
                        className='digital-greetings-select__content-action'
                        id='digital-greetings-select-content-action'
                        role="button"
                        tabIndex={0}
                        aria-label={intl.formatMessage(messageIntl.digitalGreetingsSelectLink)}
                    >
                        <TertiaryButton {...selectBtnProps} />
                    </div>
                </div>
            </Container>
        </div>
    );
};

SelectDigitalGreetings.propTypes = {
    intl: intlShape.isRequired,
    formName: PropTypes.string,
    setIsGreetingSelected: PropTypes.func.isRequired
};

SelectDigitalGreetings.defaultProps = {
    formName: NEW_ITEM_FORM_NAME
};

export default injectIntl(SelectDigitalGreetings);
